import Page from '@modules/page'
import { navigate, useParams } from "@reach/router"
import { ContextualSaveBar, Layout, PageActions } from "@shopify/polaris"
import { ContextProvider, useStore } from "./container"
import TitleCard from "./components/title"
import DiscountCard from "./components/discount"
import ProductsCard from "./components/products"
import StatusCard from "./components/status"
import { useCallback, useEffect, useState } from "react"
import API from "@helpers/api"
import { showToast } from "@plugins/toast"
import { CancelToken } from "@plugins/axios"
import { checkRequiredFields } from "@helpers/utils"
import { Skeleton } from "@modules/skeleton"
import { showConfirm } from "@plugins/confirm"
import { isEqual, omit } from 'lodash'
import DiscardModal from '../../components/discardModal'

const EditQtyDiscount = () => {
	return (
		<ContextProvider>
			<EditQtyDiscountView />
		</ContextProvider>
	)
}

const EditQtyDiscountView = () => {
	const [ready, setReady] = useState(false)
	const [saving, setSaving] = useState(false)
	let [error, setError] = useState([])
	const { pageID } = useParams()
	const { state, dispatch } = useStore()
	const source = CancelToken.source()
	const { title, _id } = state
	const [activeModal, setActiveModal] = useState(false);
	const [prevState, setPrevState] = useState({})

	const isEqualState = isEqual(omit(state, ['updatedAt']), omit(prevState, ['updatedAt']))

	const handleModalChange = useCallback(() => setActiveModal(!activeModal), [activeModal]);

	useEffect(() => {
		const handleReloadPage = (e) => {
		  e.preventDefault()
		  e.returnValue = ""
		}
		if(isEqualState === false) window.addEventListener("beforeunload", handleReloadPage)
		return () => window.removeEventListener("beforeunload", handleReloadPage)
	  }, [isEqualState])

	useEffect(() => {
		let result
		const fetchData = async () => {
			try {
				result = await API.getDetail('quantity-discount', pageID, source.token)
				console.log('Data details', result)
				if (result && result.ok && result.payload) {
					dispatch({ type: 'setData', payload: result.payload })
					if (!result.payload.hasOwnProperty("target_collection")) {
						result.payload.target_collection = ""
					}
          			setPrevState({...result.payload, discounts: [...result.payload.discounts]})
					setReady(true)
				}
			} catch (error) {
				console.log(error)
			}
		}
		fetchData()
		return () => source.cancel()
	}, [pageID])
	const handleSave = async () => {
		error = await checkRequiredFields(['title'], state)
		if (error.length > 0) {
			setError(error)
			window.scrollTo({ top: 0, behavior: 'smooth' })
			return showToast({
				message: 'Please fill in all the mandatory fields',
				error: true
			})
		}

		setSaving(true)
		const { target_collection, target_products, discounts } = state
		API.update('quantity-discount', {
			...state,
			target_products: target_products?.filter(Boolean).map(p => p.value),
			target_collection: target_collection?.value
		}).then((res) => {
			if (res.ok) {
				setPrevState({
					...prevState,
					...res.payload,
					target_products: target_products,
					target_collection: target_collection,
					discounts: [...discounts]
				})
				return showToast({
					message: 'Updated successfully'
				})
			}
		}).finally(() => setSaving(false))
	}

	const handleDelete = () => {
		showConfirm({
			title: `Delete ${title}?`,
			message: 'This can’t be undone.',
			danger: true,
			confirm: 'Delete'
		}).then(res => {
			if (res) {
				API.bulkDelete('quantity-discount', [_id]).then((res) => {
					if (res.ok) {
						showToast({
							message: 'Offer deleted'
						})
						return navigate('/quantity-discounts')
					} else {
						showToast({
							message: 'Cannot delete, please try again',
							error: true
						})
					}
				})
			}
		})
	}

	return ready ? (
		<Page
			title="Edit quantity discount"
			breadcrumbs={[{ content: 'QtyDiscounts', onAction: () => isEqualState ? navigate('/quantity-discounts') : handleModalChange() }]}
		>
			{!isEqualState && <ContextualSaveBar
				message="Unsaved changes"
				saveAction={{
					onAction: handleSave,
					loading: saving,
					disabled: false,
				}}
				discardAction={{
					onAction: () => handleModalChange()
				}}
			/>}
			<Layout>
				<Layout.Section>
					<TitleCard error={error} />
					<ProductsCard />
					<DiscountCard />
				</Layout.Section>
				<Layout.Section secondary>
					<StatusCard />
				</Layout.Section>
			</Layout>
			<PageActions
				primaryAction={{
					content: 'Save',
					onAction: handleSave,
					loading: saving
				}}
				secondaryActions={[
					{
						content: 'Delete',
						destructive: true,
						onAction: handleDelete
					},
				]}
			/>
			<DiscardModal
				activeModal={activeModal}
				handleModalChange={handleModalChange}
				backLink='/quantity-discounts'
			/>
		</Page>
	) : <Skeleton />
}

export default EditQtyDiscount
